import { localToUtcTime } from 'customer/components/policy/dates';
import { isBefore, addDays, isToday } from 'date-fns';

export const PolicyStatus = {
  Cancelled: 'Cancelled',
  InCancellation: 'In Cancellation',
  Inactive: 'Inactive',
  Future: 'Future',
  Active: 'Active'
};

export const PolicyStatusLabel = {
  [PolicyStatus.Active]: 'Active',
  [PolicyStatus.Future]: 'Future Effective',
  [PolicyStatus.InCancellation]: 'In Cancellation',
  [PolicyStatus.Cancelled]: 'Cancelled',
  [PolicyStatus.Inactive]: 'Inactive'
};

export const getPolicyStatus = (policy) => {
  const { endDate, effectiveDate, state, fullTermPolicyEndDate, transactionDate, fullTermCancel } = policy;

  if (transactionDate) {
    // transactionDate is for ODEN cancels, where the endDate would not change until policy preview, so let's just put InCancellation
    return PolicyStatus.InCancellation;
  }

  const now = new Date();
  const endDateFromPolicy = localToUtcTime(`${endDate}T23:59:59.99`, state);

  const originalEndDate = localToUtcTime(String(fullTermPolicyEndDate || endDate).concat('T23:59:59.99'), state);
  const cancelledEndDate =
    fullTermCancel || isBefore(endDateFromPolicy, originalEndDate)
      ? localToUtcTime(`${endDate}T00:01:00.00`, state)
      : undefined;

  const startDateFromPolicy = localToUtcTime(effectiveDate, state);

  // check for cancel reason or cancelled bool
  if (cancelledEndDate) {
    if (
      // add a day here to allow rescinding cancellation on canceled date
      isBefore(addDays(cancelledEndDate, 1), now) ||
      // flat cancel logic
      (endDate === effectiveDate && isToday(localToUtcTime(endDateFromPolicy)))
    ) {
      return PolicyStatus.Cancelled;
    }
    return PolicyStatus.InCancellation;
  }

  if (endDateFromPolicy < now) {
    return PolicyStatus.Inactive;
  }

  if (startDateFromPolicy > now) {
    return PolicyStatus.Future;
  }

  return PolicyStatus.Active;
};

/**
 * Display label for a policy's status. A cancelled policy that was rewritten (`rewrittenToPolicyId`,
 * set by rewritePolicy) reads "Cancelled (rewritten)" so it isn't mistaken for a plain cancellation
 * (#36661). Display only: `PolicyStatus` and everything that compares on it are unchanged. Pass
 * `status` when the caller already has it (e.g. the policy store's).
 */
export const getPolicyStatusLabel = (policy, status = getPolicyStatus(policy)) => {
  const label = PolicyStatusLabel[status] ?? status;
  return status === PolicyStatus.Cancelled && policy?.rewrittenToPolicyId ? `${label} (rewritten)` : label;
};

// Policy `source` markers written by the legacy-carrier import converters (#34115). Forreston
// imports from IMT ('imt-conversion'); CFM imports from Guidewire InsuranceNow ('guidewire-conversion').
// An imported term is read-only only when its brand also left it non-endorsable (canEndorse:false) —
// CFM opts into endorsable imports, so those carry the marker but stay editable.
export const IMPORTED_POLICY_SOURCES = ['imt-conversion', 'guidewire-conversion'];

export const isImportedPolicy = (policy) => IMPORTED_POLICY_SOURCES.includes(policy?.source);

export const isImportedAndNotEndorseable = (policy) => isImportedPolicy(policy) && !policy?.canEndorse;

// Actions a policy-settings preview can execute; the value selects the preview mutation
// (getPreviewMutationForAction). Declared by the submitting screen (getScreenAction); undefined = plain change.
export const PolicyChangeAction = {
  Reinstate: 'reinstate',
  Rescind: 'rescind',
  Cancel: 'cancel'
};
